import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface VoiceRecorderProps {
  onSend: (base64Ptt: string) => void | Promise<unknown>;
  onCancel: () => void;
}

const BAR_COUNT = 48;

export default function VoiceRecorder({ onSend, onCancel }: VoiceRecorderProps) {
  const { t } = useTranslation("dashboard");
  const [isInitializing, setIsInitializing] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioData, setAudioData] = useState<number[]>(
    Array.from({ length: BAR_COUNT }, () => 0.08),
  );
  const [frozenWave, setFrozenWave] = useState<number[] | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopAnimation = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  const startAnimation = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    let frameCount = 0;
    const SKIP_FRAMES = 5;

    const animate = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / bufferLength);
      const volume = Math.max(0.06, Math.min(1.0, rms * 5.0));

      frameCount++;
      if (frameCount >= SKIP_FRAMES) {
        frameCount = 0;
        setAudioData((prev) => [...prev.slice(1), volume]);
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioContextClass =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const audioCtx = new AudioContextClass();
      audioCtxRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      setAudioData(Array.from({ length: BAR_COUNT }, () => 0.08));
      setFrozenWave(null);
      startAnimation();

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = () => {
          onSend(reader.result as string);
        };
        reader.readAsDataURL(blob);
        stopAnimation();
        audioCtx.close();
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        analyserRef.current = null;
        audioCtxRef.current = null;
      };

      mediaRecorder.start(100);
      setIsInitializing(false);
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } catch {
      setIsInitializing(false);
      onCancel();
    }
  }, [onSend, onCancel, startAnimation, stopAnimation]);

  const handlePauseResume = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (!mr || isInitializing) return;

    if (!isPaused) {
      if (mr.state === "recording") mr.pause();
      stopTimer();
      stopAnimation();
      setFrozenWave((prev) => prev ?? audioData);
      setIsPaused(true);
    } else {
      if (mr.state === "paused") mr.resume();
      setFrozenWave(null);
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
      startAnimation();
      setIsPaused(false);
    }
  }, [isPaused, isInitializing, audioData, stopTimer, stopAnimation, startAnimation]);

  const handleSend = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (!mr || isInitializing) return;
    stopTimer();
    stopAnimation();
    if (mr.state === "paused") mr.resume();
    if (mr.state !== "inactive") mr.stop();
  }, [stopTimer, stopAnimation, isInitializing]);

  const handleDelete = useCallback(() => {
    const mr = mediaRecorderRef.current;
    stopTimer();
    stopAnimation();

    if (mr) {
      mr.ondataavailable = null;
      mr.onstop = null;
      if (mr.state !== "inactive") {
        try {
          mr.stop();
        } catch {
          // already stopped or errored — ignore
        }
      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    chunksRef.current = [];

    setIsInitializing(false);
    setIsPaused(false);
    setDuration(0);
    setAudioData(Array.from({ length: BAR_COUNT }, () => 0.08));
    setFrozenWave(null);
    onCancel();
  }, [stopTimer, stopAnimation, onCancel]);

  useEffect(() => {
    startRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      stopTimer();
      stopAnimation();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      audioCtxRef.current?.close();
    };
  }, [stopTimer, stopAnimation]);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const displayWave = frozenWave ?? audioData;

  return (
    <div className="flex items-center gap-2 w-full bg-accent-50 rounded-xl px-3 py-2 border border-accent-200/60 min-h-[48px]">
      <button
        onClick={handleDelete}
        className="w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-red-500 cursor-pointer transition-colors flex-shrink-0"
        title={t("voice.delete")}
      >
        <i className="ri-delete-bin-line text-lg"></i>
      </button>

      <div className="flex-shrink-0">
        <span
          className="block w-2 h-2 rounded-full bg-red-500"
          style={{
            animation: isPaused || isInitializing ? "none" : "recDot 1.2s ease-in-out infinite",
          }}
        />
      </div>

      <span className="text-foreground-700 text-sm font-medium tabular-nums flex-shrink-0 min-w-[36px]">
        {formatTime(duration)}
      </span>

      <div className="flex-1 flex items-center gap-[2px] h-8 min-w-0 px-1">
        {isInitializing ? (
          <span className="text-xs text-foreground-400 italic animate-pulse">{t("voice.connecting")}</span>
        ) : (
          displayWave.map((val, i) => {
            const height = Math.max(3, Math.round(val * 28));
            return (
              <div
                key={i}
                className="flex-1 rounded-full transition-all duration-75"
                style={{
                  height: `${height}px`,
                  backgroundColor: isPaused ? "#888" : "#f87171",
                  opacity: isPaused ? 0.6 : 0.85 + val * 0.15,
                }}
              />
            );
          })
        )}
      </div>

      <button
        onClick={handlePauseResume}
        disabled={isInitializing}
        className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-600 disabled:text-foreground-300 cursor-pointer transition-colors flex-shrink-0"
        title={isPaused ? t("voice.resume") : t("voice.pause")}
      >
        {isPaused ? (
          <i className="ri-play-fill text-lg"></i>
        ) : (
          <i className="ri-pause-fill text-lg"></i>
        )}
      </button>

      <button
        onClick={handleSend}
        disabled={isInitializing}
        className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 hover:bg-accent-600 disabled:bg-background-200 text-white cursor-pointer transition-colors flex-shrink-0"
        title={t("voice.send")}
      >
        <i className="ri-send-plane-fill text-sm translate-x-0.5"></i>
      </button>

      <style>{`
        @keyframes recDot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.15; }
        }
      `}</style>
    </div>
  );
}